import {
  AfterViewInit,
  Component,
  DestroyRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import {
  Answers,
  AnswerValue,
  isLifemapTypeBE,
  LifemapService,
  LifemapType,
  LifemapTypeBE,
  PlCalendarEvent,
  StartEndDateTime,
} from '@shared/services/lifemap/lifemap.service';
import { ButtonPadding, ButtonVariant, PlButtonType } from '@app/components/button/button.component';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { addMinutes, differenceInMinutes, format } from 'date-fns';
import { I18nService } from '@app/i18n';
import { BlockLifemap } from '@shared/models/step.model';
import { ComponentChanges } from '@app/utils/simpleChangesType';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconFrame } from '@app/icon-library/lib/pl-icon.component';
import { PlIcon } from '@app/icon-library/icons';
import { plValidateLifemapQuestions } from '@app/components/blocks/block-lifemap/lifemapQuestions/lifemapQuestionValidator';
import { PlTimepickerComponent, TimeFormat } from '@app/components/pl-timepicker/pl-timepicker.component';
import { roundToNearestHalfHour } from '@app/utils/date';

interface LifemapTypeToSelect {
  id: LifemapTypeBE;
  label: string;
  icon: PlIcon;
}

enum FormControlName {
  activityName = 'activityName',
  answers = 'answers',
  timeFrom = 'timeFrom',
  timeTo = 'timeTo',
  type = 'type',
}

interface EditLifemapEntryForm {
  [FormControlName.activityName]: FormControl<string>;
  [FormControlName.answers]: FormControl<Answers>;
  [FormControlName.timeFrom]: FormControl<string>;
  [FormControlName.timeTo]: FormControl<string>;
  [FormControlName.type]: FormControl<LifemapType>;
}

export interface LifemapEntryData {
  eventId: string | number;
  activityName: string;
  answers?: Answers;
  timeFrom: string;
  type: LifemapType;
  timeTo: string;
}

enum CustomErrors {
  durationTooLong = 'durationTooLong',
  sameFromAndToTime = 'sameFromAndToTime',
}

@Component({
  selector: 'pl-edit-lifemap-entry',
  templateUrl: './editLifemapEntry.component.html',
  animations: [],
})
export class EditLifemapEntryComponent implements AfterViewInit, OnChanges {
  @Input({ required: true }) public event!: PlCalendarEvent | null;
  @Input({ required: true }) public blockLifemap!: BlockLifemap;
  @Input({ required: true }) public timeFormat!: TimeFormat;
  @Input({ required: true }) public allActivities: string[] = [];

  @Output() public formChanges: EventEmitter<LifemapEntryData> = new EventEmitter<LifemapEntryData>();
  @Output() public discardButtonClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output() public saveButtonClicked: EventEmitter<LifemapEntryData> = new EventEmitter<LifemapEntryData>();

  public dayNumberCurrentLifeMapEntryEdited: WritableSignal<number> = signal(0);
  public displayQuestionsAndActivityNameDropdown: WritableSignal<boolean> = signal(false);
  public displaySnackBar: WritableSignal<boolean> = signal(false);
  public displayWarningTimeToOnFollowingDay: WritableSignal<boolean> = signal(false);
  public editLifeMapEntryForm!: FormGroup<EditLifemapEntryForm>;
  public eventIsAddNewEntry: WritableSignal<boolean> = signal(false);
  public lifemapTypes: WritableSignal<LifemapTypeToSelect[]> = this.getLifemapTypesToSelect();

  public mainTitle: string = '';

  @HostListener('document:keyup.escape', ['$event'])
  public handleKeyboardEvent(): void {
    this.discardClicked();
  }

  @ViewChild('timePickerTo') public timePickerTo!: PlTimepickerComponent;

  protected readonly ButtonPadding: typeof ButtonPadding = ButtonPadding;
  protected readonly ButtonVariant: typeof ButtonVariant = ButtonVariant;
  protected readonly CustomErrors: typeof CustomErrors = CustomErrors;
  protected readonly FormControlName: typeof FormControlName = FormControlName;
  protected readonly IconFrame: typeof IconFrame = IconFrame;
  protected readonly LifemapType: typeof LifemapType = LifemapType;
  protected readonly PlButtonType: typeof PlButtonType = PlButtonType;
  protected readonly PlIcon: typeof PlIcon = PlIcon;
  protected readonly TimeFormat: typeof TimeFormat = TimeFormat;

  protected readonly MINUTES_GAP: number = 30;

  protected destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    private i18nService: I18nService,
    public lifemapService: LifemapService,
  ) {
    this.createForm();
    this.watchTypeChange();
    this.watchFormChanges();
  }

  public ngAfterViewInit(): void {
    if (this.event?.meta.type === LifemapType.addNewEntry) {
      if (this.timePickerTo) {
        this.timePickerTo.openDropdownToSelectTime();
      }
    }
  }

  public ngOnChanges(changes: ComponentChanges<EditLifemapEntryComponent>): void {
    console.log('\x1b[44m TONIO EditLifemapEntryComponent ngOnChanges changes=', changes, '\x1b');
    if (changes.event) {
      const event: PlCalendarEvent | null = changes.event.currentValue;
      if (event) {
        if (this.hasCalendarDataChanged(event)) {
          this.parseCalendarEvent(event);
        }

        if (changes.event.firstChange) {
          this.eventIsAddNewEntry.set(event.meta.type === LifemapType.addNewEntry);
        }
      } else {
        throw new Error(`Event should not be null`);
      }
    }
  }

  /**
   * When user complete the form in this component, we emit changes to update the calendar
   * We update the color, timeFrom, timeTo and activityName on the calendar.
   * Doing this update triggers ngOnChanges, so in order to avoid unnecessary computation, we
   * check in this function if the data displayed on the form is different than the event
   * @param event
   * @private
   */
  private hasCalendarDataChanged(event: PlCalendarEvent): boolean {
    if (format(event.start, 'HH:mm') !== this.editLifeMapEntryForm.controls[FormControlName.timeFrom].value) {
      console.log(
        'TONIO EditLifemapEntryComponent hasCalendarDataChanged startDate is different event.start=',
        format(event.start, 'HH:mm'),
        '  editLifeMapEntryForm.timeFrom=',
        this.editLifeMapEntryForm.controls[FormControlName.timeFrom].value,
      );
      return true;
    }

    if (format(event.end, 'HH:mm') !== this.editLifeMapEntryForm.controls[FormControlName.timeTo].value) {
      console.log(
        'TONIO EditLifemapEntryComponent hasCalendarDataChanged endDate is different event.end=',
        format(event.start, 'HH:mm'),
        '  editLifeMapEntryForm.timeTo=',
        this.editLifeMapEntryForm.controls[FormControlName.timeTo].value,
      );
      return true;
    }

    if (event.meta.type !== this.editLifeMapEntryForm.controls[FormControlName.type].value) {
      console.log(
        'TONIO EditLifemapEntryComponent hasCalendarDataChanged type is different event.meta.type=',
        event.meta.type,
        '  editLifeMapEntryForm.type=',
        this.editLifeMapEntryForm.controls[FormControlName.type].value,
      );
      return true;
    }

    if (event.meta.type === LifemapType.work || event.meta.type === LifemapType.nonWork) {
      if (event.title !== this.editLifeMapEntryForm.controls[FormControlName.activityName].value) {
        console.log(
          'TONIO EditLifemapEntryComponent hasCalendarDataChanged activityName is different event.title=',
          event.title,
          '  editLifeMapEntryForm.activityName=',
          this.editLifeMapEntryForm.controls[FormControlName.activityName].value,
        );
        return true;
      }

      return this.hasAnswersChanged(
        event.meta.answers,
        this.editLifeMapEntryForm.controls[FormControlName.answers].value,
      );
    }

    return false;
  }

  private hasAnswersChanged(eventAnswers: Answers | undefined, formAnswers: Answers): boolean {
    console.log(
      'TONIO EditLifemapEntryComponent hasAnswersChanged eventAnswers=',
      eventAnswers,
      '   formAnswers=',
      formAnswers,
    );
    if (typeof eventAnswers === 'undefined') {
      const areAllFormAnswersNull: boolean = !!formAnswers.find(
        (singleAnswer: AnswerValue): boolean => singleAnswer !== null,
      );
      return areAllFormAnswersNull;
    }

    for (let index: number = 0; index < eventAnswers.length; index++) {
      const eventAnswer: AnswerValue = eventAnswers[index];
      const formAnswer: AnswerValue = formAnswers[index];
      if (eventAnswer !== formAnswer) {
        console.log(
          'TONIO EditLifemapEntryComponent hasAnswersChanged for index=',
          index,
          'it has changed eventAnswer=',
          eventAnswer,
          '   formAnswer=',
          formAnswer,
        );
        return true;
      }
    }

    return false;
  }

  private parseCalendarEvent(event: PlCalendarEvent): void {
    console.log('\x1b[41m TONIO EditLifemapEntryComponent parseCalendarEvent event=', event, '\x1b');
    if (event) {
      const dayDiff: number = this.lifemapService.getCalendarsDaysDiff(
        event.start,
        this.lifemapService.getCalendarStartDate(),
      );
      this.dayNumberCurrentLifeMapEntryEdited.set(dayDiff);

      let activityName: string = '';
      if (isLifemapTypeBE(event.meta.type)) {
        activityName = event.title;
        this.mainTitle = this.i18nService.translate(`lifemap.edit_entry`);
      } else {
        this.mainTitle = this.i18nService.translate(`lifemap.new_entry`);
      }

      this.editLifeMapEntryForm.controls[FormControlName.activityName].setValue(activityName, { emitEvent: false });
      this.editLifeMapEntryForm.controls[FormControlName.answers].setValue(
        event.meta?.answers || [null, null, null, null, null, null, null, null, null, null],
        { emitEvent: false },
      );

      const type: LifemapType = event.meta.type;

      this.editLifeMapEntryForm.controls[FormControlName.type].setValue(type, { emitEvent: false });
      if (isLifemapTypeBE(type)) {
        this.displayQuestionsAndActivityNameDropdown.set(type !== LifemapType.sleep);
        this.editLifeMapEntryForm.controls[FormControlName.activityName].setValidators(
          type === LifemapType.sleep ? null : Validators.required,
        );
        this.showSnackbar();
      } else {
        this.displayQuestionsAndActivityNameDropdown.set(false);
        this.hideSnackbar();
      }

      this.editLifeMapEntryForm.controls[FormControlName.timeFrom].setValue(format(event.start, 'HH:mm'), {
        emitEvent: false,
      });

      const endTime: string = format(roundToNearestHalfHour(event.end as Date), 'HH:mm');
      this.editLifeMapEntryForm.controls[FormControlName.timeTo].setValue(endTime, { emitEvent: false });
    } else {
      throw new Error(`Event should not be null`);
    }
  }

  private watchTypeChange(): void {
    this.editLifeMapEntryForm.controls[FormControlName.type].valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (type: LifemapType): void => {
          if (isLifemapTypeBE(type)) {
            this.showSnackbar();
            this.displayQuestionsAndActivityNameDropdown.set(type !== LifemapType.sleep);
          } else {
            this.displayQuestionsAndActivityNameDropdown.set(false);
          }

          console.log(
            'TONIO EditLifemapEntryComponent next type=',
            type,
            '  this.editLifeMapEntryForm=',
            this.editLifeMapEntryForm,
          );
          this.editLifeMapEntryForm.controls[FormControlName.activityName].setValidators(
            type === LifemapType.sleep ? null : Validators.required,
          );
          this.editLifeMapEntryForm.controls[FormControlName.answers].setValidators(
            type === LifemapType.sleep ? null : plValidateLifemapQuestions,
          );
          this.editLifeMapEntryForm.controls[FormControlName.activityName].updateValueAndValidity({ emitEvent: false });
          this.editLifeMapEntryForm.controls[FormControlName.answers].updateValueAndValidity({ emitEvent: false });
        },
      });
  }

  private watchFormChanges(): void {
    this.editLifeMapEntryForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (): void => {
        const editEntryData: LifemapEntryData = this.getEditLifemapData();
        this.formChanges.emit(editEntryData);
      },
    });

    this.editLifeMapEntryForm.controls[FormControlName.timeFrom].valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (timeFrom: string): void => {
          const timeTo: string = this.editLifeMapEntryForm.controls[FormControlName.timeTo].value;
          if (timeFrom >= timeTo) {
            const timeFromAsDate: Date = new Date(`2024-01-29T${timeFrom}:00`);
            const timeToJustAfterTimeFrom: Date = addMinutes(timeFromAsDate, this.MINUTES_GAP);
            this.editLifeMapEntryForm.controls[FormControlName.timeTo].setValue(
              format(timeToJustAfterTimeFrom, 'HH:mm'),
            );
          }
        },
      });
  }

  private createForm(): void {
    const fb: FormBuilder = new FormBuilder();
    this.editLifeMapEntryForm = fb.group<EditLifemapEntryForm>({
      [FormControlName.activityName]: fb.nonNullable.control<string>('', [Validators.required]),
      [FormControlName.answers]: fb.nonNullable.control<Answers>(
        [null, null, null, null, null, null, null, null, null, null],
        [],
      ),
      [FormControlName.timeFrom]: fb.nonNullable.control<string>(''),
      [FormControlName.timeTo]: fb.nonNullable.control<string>(''),
      [FormControlName.type]: fb.nonNullable.control<LifemapType>(LifemapType.addNewEntry, [Validators.required]),
    });

    this.editLifeMapEntryForm.setValidators([this.validateTimesFromAndTo.bind(this)]);
  }

  private validateTimesFromAndTo(): ValidationErrors | null {
    const timeFrom: string = this.editLifeMapEntryForm.controls[FormControlName.timeFrom].value;
    const timeTo: string = this.editLifeMapEntryForm.controls[FormControlName.timeTo].value;

    this.displayWarningTimeToOnFollowingDay.set(timeTo < timeFrom);

    if (timeFrom !== '' && timeTo !== '') {
      const eventStartDate: Date = this.event?.start as Date;
      const { startDateTime, endDateTime }: StartEndDateTime = this.lifemapService.computeNewStartEndDatesBasedOnTime(
        eventStartDate,
        timeFrom,
        timeTo,
      );
      const numberMinutesDiff: number = differenceInMinutes(endDateTime, startDateTime);
      const error: ValidationErrors = {};
      if (numberMinutesDiff > 12 * 60) {
        error[CustomErrors.durationTooLong] = +(numberMinutesDiff / 60).toFixed(1);
      } else if (numberMinutesDiff === 0) {
        error[CustomErrors.sameFromAndToTime] = true;
      }

      if (Object.keys(error).length > 0) {
        this.editLifeMapEntryForm.controls[FormControlName.timeFrom].setErrors(error);
        this.editLifeMapEntryForm.controls[FormControlName.timeTo].setErrors(error);
        this.editLifeMapEntryForm.controls[FormControlName.timeFrom].markAsDirty();
        this.editLifeMapEntryForm.controls[FormControlName.timeTo].markAsDirty();
        return error;
      }
    }

    this.editLifeMapEntryForm.controls[FormControlName.timeFrom].setErrors(null);
    this.editLifeMapEntryForm.controls[FormControlName.timeTo].setErrors(null);
    return null;
  }

  private getLifemapTypesToSelect(): WritableSignal<LifemapTypeToSelect[]> {
    return signal([
      {
        id: LifemapType.sleep,
        label: this.i18nService.translate(`lifemap.category.${LifemapType.sleep}`),
        icon: PlIcon.moon,
      },
      {
        id: LifemapType.work,
        label: this.i18nService.translate(`lifemap.category.${LifemapType.work}`),
        icon: PlIcon.briefcase,
      },
      {
        id: LifemapType.nonWork,
        label: this.i18nService.translate(`lifemap.category.${LifemapType.nonWork}`),
        icon: PlIcon.heart_wind,
      },
    ]);
  }

  public onCategorySelected(categorySelected: LifemapTypeBE): void {
    this.editLifeMapEntryForm.controls[FormControlName.type].setValue(categorySelected);
  }

  public saveLifeMapEntry(): void {
    const editEntryData: LifemapEntryData = this.getEditLifemapData();
    console.log('\x1b[41m TONIO EditLifemapEntryComponent saveLifeMapEntry editEntryData=', editEntryData, '\x1b');

    if (this.editLifeMapEntryForm.valid) {
      this.saveButtonClicked.emit(editEntryData);
    } else {
      this.editLifeMapEntryForm.controls[FormControlName.activityName].markAsDirty();
      this.editLifeMapEntryForm.controls[FormControlName.answers].markAsDirty();
      this.editLifeMapEntryForm.controls[FormControlName.timeFrom].markAsDirty();
      this.editLifeMapEntryForm.controls[FormControlName.timeTo].markAsDirty();
      this.editLifeMapEntryForm.controls[FormControlName.type].markAsDirty();
    }
  }

  private getEditLifemapData(): LifemapEntryData {
    const activityName: string = this.editLifeMapEntryForm.controls[FormControlName.activityName].value as string;
    const answers: Answers = this.editLifeMapEntryForm.controls[FormControlName.answers].value as Answers;
    const timeFrom: string = this.editLifeMapEntryForm.controls[FormControlName.timeFrom].value as string;
    const timeTo: string = this.editLifeMapEntryForm.controls[FormControlName.timeTo].value as string;
    const type: LifemapTypeBE | null = this.editLifeMapEntryForm.controls[FormControlName.type].value as LifemapTypeBE;

    return {
      eventId: this.event?.id || '',
      activityName: type !== LifemapType.sleep ? activityName : '',
      type,
      timeFrom,
      timeTo,
      answers: type !== LifemapType.sleep ? answers : undefined,
    };
  }

  public hideSnackbar(): void {
    this.displaySnackBar.set(false);
  }

  public showSnackbar(): void {
    this.displaySnackBar.set(true);
  }

  public discardClicked(): void {
    this.discardButtonClicked.emit();
  }
}
